import { Coordinate } from 'ol/coordinate';

export interface GPSLocation {
  latitude: number;
  longitude: number;
}

export enum SliderType {
  'POINT_OF_TIME' = 'pointOfTime',
  'PERIOD_OF_TIME' = 'periodOfTime'
}

// The map's Field Data tab (FMW-547): what machines logged in the field, as
// opposed to the four per-block measurement tabs.
export const FIELD_DATA_TAB = 'Field Data';
// The Pick Count tab (FMW-606): the fruit the harvester picked, its own
// dataset beside the breadcrumbs.
export const PICKED_COUNT_TAB = 'Pick Count';

export enum DynamicLayerName {
  FLOWERING_X = 'Elongation (X)',
  FLOWERING_Y = 'Christmas tree (Y)',
  FLOWERING_Z = 'Flower drop (Z)',
  FLOWERING_XY = 'Flowering XY',
  FLOWERING_YZ = 'Flowering YZ',
  FLOWERING_XYZ = 'Flowering XYZ',
  FLOWERING_PHOTO = 'Flowering Photo',
  FRUIT_COUNT = 'Fruit Count',
  FRUIT_PHOTO = 'Fruit Photo',
  FIELD_BINS = 'Field Bins',
  DRY_MATTER = 'Dry Matter',
  DRY_MATTER_BLOCK = 'Dry Matter Block',
  FRUIT_SIZE = 'Fruit Size',
  FRUIT_SIZE_PHOTO = 'Fruit Size Photo',
  HARVESTER_BREADCRUMBS = 'Harvester Breadcrumbs',
  PICKED_COUNT = 'Pick Count',
  // Satellite canopy vigour (FMW-635), shown instead of a count layer.
  NDVI = 'NDVI (Sentinel-2)'
}
// How the main map draws the picked dynamic layer (FMW-605): its dots or its
// heat map.
export type DynamicLayerDisplay = 'dots' | 'heatmap';
// The Fruit group's state as the maps read it. `current` is null while the
// group is unchecked; `type` is the picked DynamicLayerName.
export interface CurrentDynamicLayer {
  current: null | string;
  type: string;
  display: DynamicLayerDisplay;
}

// The map's bottom tabs, in order: a q-tab name and its label.
export interface MapTab {
  name: string;
  label: string;
}
export const MAP_TABS: readonly MapTab[] = [
  { name: 'Flowering', label: 'Flower Count' },
  { name: 'Fruit Count', label: 'Fruit Count' },
  { name: PICKED_COUNT_TAB, label: 'Pick Count' },
  { name: 'Fruit Size', label: 'Fruit Size' },
  { name: 'Dry Matter', label: 'Internal Quality' },
  { name: FIELD_DATA_TAB, label: 'Field Data' }
];

// Each tab's dynamic layers, as its radio list offers them.
export const DYNAMIC_LAYERS_BY_TAB: { [tab: string]: string[] } = {
  Flowering: [
    DynamicLayerName.FLOWERING_X,
    DynamicLayerName.FLOWERING_Y,
    DynamicLayerName.FLOWERING_Z,
    DynamicLayerName.FLOWERING_XY,
    DynamicLayerName.FLOWERING_YZ,
    DynamicLayerName.FLOWERING_XYZ,
    DynamicLayerName.NDVI
  ],
  'Fruit Count': [DynamicLayerName.FRUIT_COUNT, DynamicLayerName.FIELD_BINS, DynamicLayerName.NDVI],
  'Fruit Size': [DynamicLayerName.FRUIT_SIZE],
  'Dry Matter': [DynamicLayerName.DRY_MATTER, DynamicLayerName.DRY_MATTER_BLOCK],
  [FIELD_DATA_TAB]: [DynamicLayerName.HARVESTER_BREADCRUMBS],
  [PICKED_COUNT_TAB]: [DynamicLayerName.PICKED_COUNT]
};

/** The layer a tab opens on: the combined flowering stages, else its first. */
export function defaultLayerForTab(tab: string): string {
  if (tab == 'Flowering') return DynamicLayerName.FLOWERING_XYZ;
  return DYNAMIC_LAYERS_BY_TAB[tab]?.[0] ?? '';
}
// One field photo on a photo map layer (Flowering / Fruit / Fruit Size Photo),
// as browsed by the fullscreen viewer and the identify popup gallery.
//
// `label` names the location the photo was taken at -- each photo SQL view
// exposes a different identifier column for it (tree name / frame number /
// frame id). `coordinate` is EPSG:4326 [lon, lat] so the popup can re-anchor
// and the map can recentre when navigation crosses to another location.
export interface FieldPhoto {
  photoPath: string;
  photo: string;
  thumbnail: string;
  date: string;
  label: string;
  coordinate: Coordinate | null;
}

export interface PointGeom {
  type: string;
  coordinates: Coordinate;
  properties?: {
    [key: string]: unknown;
  };
}
export interface Geom {
  type: string;
  coordinates: Coordinate | Coordinate[] | Coordinate[][] | Coordinate[][][];
  properties?: {
    [key: string]: unknown;
  };
}

// LayerInterface and LayerGroupInterface come back together in MapStore.Layers
// (and MapStore.BaseLayers). The /api/grouped-layers/ response interleaves
// standalone layers with group containers, and the UI iterates the combined
// array — so each interface carries the *other* shape's fields as optional,
// rather than forcing every template access to narrow on a discriminator.
export interface LayerInterface {
  id: number;
  order: number;
  url: string;
  baseUrl: string;
  layer: string;
  layerType: string;
  layerId: string;
  layerTitle: string;
  srs: string;
  defaultVisible: boolean;
  legendUrl: string;
  zIndex: number;
  layerDescription: string;
  sourceDescription: string;
  // Group fields — undefined on standalone layers
  groupId?: number;
  groupType?: string;
  defaultSelected?: string;
  layers?: LayerInterface[];
  name?: string;
}

export interface LayerGroupInterface {
  id: number;
  groupId: number;
  order: number;
  name: string;
  groupType: string;
  defaultVisible: boolean;
  defaultSelected?: string;
  layers?: LayerInterface[];
  // Standalone-layer fields — undefined on groups
  url?: string;
  baseUrl?: string;
  layer?: string;
  layerType?: string;
  layerId?: string;
  layerTitle?: string;
  srs?: string;
  legendUrl?: string;
  zIndex?: number;
  layerDescription?: string;
  sourceDescription?: string;
}
